<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import ProblemCategorySelect from '@shared/ui/ProblemCategorySelect.svelte';
  import ProblemThemeSelect from '@shared/ui/ProblemThemeSelect.svelte';
  import { upsertProductProblemLink } from '@shared/api/products';
  import { listProblems } from '@shared/api/problems';
  import {
    fetchUnlinkedProblemCandidates,
    PROBLEM_CANDIDATE_VISIBLE_LIMIT,
    problemPath,
    type ProblemCandidateFilters,
  } from './problem-candidates';
  import type {
    ProductProblemLink,
    ProductProblemLinkUpsertRequest,
  } from '@shared/types/product-problem-link';
  import type { Problem } from '@shared/types/problem';

  const DEFAULT_FIT_LEVEL = 3;

  interface Props {
    open: boolean;
    productId: string;
    excludeIds?: string[];
    onClose: () => void;
    onUpserted: (link: ProductProblemLink, problem?: Problem) => void;
  }

  let { open, productId, excludeIds = [], onClose, onUpserted }: Props = $props();

  let selectedCategory = $state('');
  let selectedTheme = $state('');
  let problemSearchQuery = $state('');
  let problemId = $state('');
  let submitting = $state(false);
  let serverError = $state<string | null>(null);
  let candidates = $state<Problem[]>([]);
  let candidatesLoading = $state(false);
  let candidatesError = $state<string | null>(null);
  let candidateRequestSeq = 0;

  let visibleCandidates = $derived.by(() =>
    candidates.slice(0, PROBLEM_CANDIDATE_VISIBLE_LIMIT),
  );
  let hiddenCandidateCount = $derived.by(() => candidates.length - visibleCandidates.length);

  $effect(() => {
    if (!open) return;
    const filters: ProblemCandidateFilters = {
      category: selectedCategory,
      theme: selectedTheme,
      q: problemSearchQuery,
    };
    const seq = ++candidateRequestSeq;
    const excluded = [...excludeIds];
    candidatesLoading = true;
    candidatesError = null;
    candidates = [];
    const delayMs = filters.q.trim() === '' ? 0 : 250;
    const timer = setTimeout(() => {
      void loadCandidates(seq, filters, excluded);
    }, delayMs);
    return () => clearTimeout(timer);
  });

  $effect(() => {
    if (problemId === '') return;
    if (!candidates.some((p) => p.id === problemId)) problemId = '';
  });

  async function loadCandidates(
    seq: number,
    filters: ProblemCandidateFilters,
    excluded: string[],
  ): Promise<void> {
    try {
      const res = await fetchUnlinkedProblemCandidates({
        filters,
        excludeIds: excluded,
        listProblems,
      });
      if (seq !== candidateRequestSeq) return;
      candidates = res.items;
    } catch (err) {
      if (seq !== candidateRequestSeq) return;
      candidates = [];
      candidatesError = err instanceof Error ? err.message : String(err);
    } finally {
      if (seq === candidateRequestSeq) candidatesLoading = false;
    }
  }

  function reset() {
    selectedCategory = '';
    selectedTheme = '';
    problemSearchQuery = '';
    problemId = '';
    submitting = false;
    serverError = null;
    candidatesError = null;
    candidateRequestSeq += 1;
    candidatesLoading = false;
  }

  function close() {
    reset();
    onClose();
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    if (!problemId) {
      serverError = '課題を選択してください';
      return;
    }
    if (!candidates.some((p) => p.id === problemId)) {
      serverError = '候補から課題を選択してください';
      return;
    }
    const selectedProblem = candidates.find((p) => p.id === problemId);
    const body: ProductProblemLinkUpsertRequest = {
      fit_level: DEFAULT_FIT_LEVEL,
      notes: null,
    };
    submitting = true;
    serverError = null;
    try {
      const link = await upsertProductProblemLink(productId, problemId, body);
      onUpserted(link, selectedProblem);
      reset();
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }

  function handleCategoryChange(category: string): void {
    selectedCategory = category;
    selectedTheme = '';
    problemId = '';
  }

  function handleThemeChange(theme: string): void {
    selectedTheme = theme;
    problemId = '';
  }
</script>

<Modal {open} title="課題を紐づける" onClose={close}>
  <form id="link-add-form" onsubmit={handleSubmit}>
    <div class="filter-grid">
      <div class="field">
        <span>大カテゴリ</span>
        <ProblemCategorySelect
          value={selectedCategory}
          onChange={handleCategoryChange}
          placeholderLabel="大カテゴリ (すべて)"
        />
      </div>
      <div class="field">
        <span>中テーマ</span>
        <ProblemThemeSelect
          category={selectedCategory}
          value={selectedTheme}
          onChange={handleThemeChange}
          placeholderLabel="中テーマ (すべて)"
        />
      </div>
      <label class="field">
        <span>小課題名検索</span>
        <input
          type="search"
          placeholder="小課題名で検索"
          bind:value={problemSearchQuery}
          autocomplete="off"
        />
      </label>
    </div>

    <section class="candidate-section" aria-label="未紐づけ課題候補">
      <div class="candidate-heading">
        <span>候補</span>
        <small>{candidates.length}件</small>
      </div>
      {#if candidatesLoading}
        <p class="empty-candidates">候補を読み込んでいます。</p>
      {:else if candidatesError}
        <p class="server-error">{candidatesError}</p>
      {:else if candidates.length === 0}
        <p class="empty-candidates">紐づけ可能な課題がありません</p>
      {:else}
        <div class="candidate-list" data-testid="product-problem-link-candidates">
          {#each visibleCandidates as candidate (candidate.id)}
            <button
              type="button"
              class:selected={problemId === candidate.id}
              aria-pressed={problemId === candidate.id}
              disabled={submitting || candidatesLoading}
              onclick={() => (problemId = candidate.id)}
              data-testid="product-problem-link-candidate"
            >
              <span class="candidate-path">{problemPath(candidate)}</span>
              <span class="candidate-name">{candidate.name}</span>
            </button>
          {/each}
        </div>
        {#if hiddenCandidateCount > 0}
          <small class="muted">
            さらに {hiddenCandidateCount} 件あります。条件を絞り込んでください。
          </small>
        {/if}
      {/if}
    </section>

    {#if serverError}
      <p class="server-error">{serverError}</p>
    {/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}>キャンセル</button>
    <button
      type="submit"
      form="link-add-form"
      class="primary"
      disabled={submitting || candidatesLoading || problemId === ''}
    >
      {submitting ? '保存中…' : '課題を紐づけ'}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .filter-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px 12px;
  }
  label,
  .field {
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
    color: #374151;
    gap: 4px;
    min-width: 0;
  }
  label > span,
  .field > span {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }
  input {
    width: 100%;
    box-sizing: border-box;
    padding: 7px 9px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
  }
  .field :global(select) {
    width: 100%;
    box-sizing: border-box;
    padding: 7px 9px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
    font: inherit;
    font-size: 13px;
  }
  .candidate-section {
    display: grid;
    gap: 8px;
  }
  .candidate-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #374151;
    font-size: 13px;
    font-weight: 700;
  }
  .candidate-heading small {
    color: #64748b;
    font-weight: 600;
  }
  .candidate-list {
    display: grid;
    gap: 6px;
    max-height: 320px;
    overflow-y: auto;
  }
  .candidate-list button {
    display: grid;
    gap: 3px;
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
    text-align: left;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    color: #111827;
    cursor: pointer;
    font: inherit;
  }
  .candidate-list button:hover:not(:disabled) {
    border-color: #1d4ed8;
    background: #eff6ff;
  }
  .candidate-list button.selected {
    border-color: #1d4ed8;
    background: #eff6ff;
    box-shadow: 0 0 0 1px #1d4ed8 inset;
  }
  .candidate-list button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .candidate-path {
    color: #64748b;
    font-size: 11px;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }
  .candidate-name {
    color: #111827;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }
  .empty-candidates {
    margin: 0;
    padding: 16px;
    color: #64748b;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
  }
  small.muted {
    color: #6b7280;
    font-size: 12px;
  }
  .server-error {
    color: #b91c1c;
    margin: 8px 0 0;
  }
  button {
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  @media (max-width: 720px) {
    .filter-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
